import { useState, useEffect } from "react";
import { X, Plus, Trash2, Loader2, Package, Pencil, Check, ArrowUpDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  created_at: string;
  sort_order?: number;
}

interface ProductCategoryManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const AVAILABLE_ICONS = [
  { value: "smartphone", label: "Smartphone" },
  { value: "laptop", label: "Laptop" },
  { value: "tablet", label: "Tablet" },
  { value: "headphones", label: "Cuffie" },
  { value: "watch", label: "Orologio" },
  { value: "tv", label: "TV" },
  { value: "camera", label: "Fotocamera" },
  { value: "gamepad-2", label: "Gaming" },
  { value: "cpu", label: "Componenti" },
  { value: "hard-drive", label: "Storage" },
];

const ProductCategoryManager = ({ isOpen, onClose }: ProductCategoryManagerProps) => {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryIcon, setNewCategoryIcon] = useState("smartphone");
  const [isSaving, setIsSaving] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState<ProductCategory | null>(null);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [editName, setEditName] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [movingCategory, setMovingCategory] = useState<ProductCategory | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("product_categories")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name");

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching product categories:", error);
      toast.error("Errore nel caricamento delle categorie prodotti");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Inserisci un nome per la categoria");
      return;
    }

    setIsSaving(true);
    try {
      const slug = generateSlug(newCategoryName);
      const maxOrder = Math.max(...categories.map(c => c.sort_order || 0), 0);
      
      const { error } = await supabase.from("product_categories").insert({
        name: newCategoryName.trim(),
        slug,
        icon: newCategoryIcon,
        sort_order: maxOrder + 1,
      });

      if (error) throw error;

      toast.success("Categoria prodotti creata!");
      setNewCategoryName("");
      setNewCategoryIcon("smartphone");
      fetchCategories();
    } catch (error) {
      console.error("Error creating product category:", error);
      toast.error("Errore nella creazione della categoria");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return;

    try {
      // Update products to remove category reference
      await supabase
        .from("products")
        .update({ category_id: null })
        .eq("category_id", deleteCategory.id);

      const { error } = await supabase
        .from("product_categories")
        .delete()
        .eq("id", deleteCategory.id);

      if (error) throw error;

      toast.success("Categoria eliminata!");
      fetchCategories();
    } catch (error) {
      console.error("Error deleting product category:", error);
      toast.error("Errore nell'eliminazione della categoria");
    } finally {
      setDeleteCategory(null);
    }
  };

  const handleStartEdit = (category: ProductCategory) => {
    setEditingCategory(category);
    setEditName(category.name);
    setEditIcon(category.icon || "smartphone");
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editName.trim()) return;

    try {
      const { error } = await supabase
        .from("product_categories")
        .update({
          name: editName.trim(),
          slug: generateSlug(editName),
          icon: editIcon,
        })
        .eq("id", editingCategory.id);

      if (error) throw error;

      toast.success("Categoria aggiornata!");
      setEditingCategory(null);
      setEditName("");
      setEditIcon("");
      fetchCategories();
    } catch (error) {
      console.error("Error updating product category:", error);
      toast.error("Errore nell'aggiornamento della categoria");
    }
  };

  const handleStartMove = (category: ProductCategory) => {
    setMovingCategory(category);
    setSelectedPosition("");
  };

  const handleConfirmMove = async () => {
    if (!movingCategory || !selectedPosition) return;

    const targetIndex = parseInt(selectedPosition);
    const currentIndex = categories.findIndex(c => c.id === movingCategory.id);

    if (targetIndex === currentIndex) {
      setMovingCategory(null);
      return;
    }

    try {
      // Reorder in local state
      const newCategories = [...categories];
      const [moved] = newCategories.splice(currentIndex, 1);
      newCategories.splice(targetIndex, 0, moved);

      // Update all sort_orders in database
      for (let i = 0; i < newCategories.length; i++) {
        await supabase
          .from("product_categories")
          .update({ sort_order: i })
          .eq("id", newCategories[i].id);
      }

      toast.success("Posizione aggiornata!");
      fetchCategories();
    } catch (error) {
      console.error("Error moving category:", error);
      toast.error("Errore nello spostamento della categoria");
    } finally {
      setMovingCategory(null);
    }
  };

  const getPositionOptions = () => {
    return categories.map((c, index) => ({
      value: index.toString(),
      label: `${index + 1}. ${c.name}`,
      isCurrent: movingCategory?.id === c.id
    }));
  };

  const renderCategoryItem = (category: ProductCategory, index: number) => {
    const isEditing = editingCategory?.id === category.id;
    const canMoveUp = index > 0;
    const canMoveDown = index < categories.length - 1;

    return (
      <motion.div
        key={category.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center justify-between p-3 rounded-xl hover:bg-muted/50 transition-colors group"
      >
        <div className="flex items-center gap-2 flex-1">
          <Package className="w-4 h-4 text-primary" />
          
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-8 text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveEdit();
                  if (e.key === "Escape") setEditingCategory(null);
                }}
              />
              <Select value={editIcon} onValueChange={setEditIcon}>
                <SelectTrigger className="w-32 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_ICONS.map(icon => (
                    <SelectItem key={icon.value} value={icon.value}>
                      {icon.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={handleSaveEdit}
                className="p-1.5 rounded-full text-green-500 hover:bg-green-500/10 transition-all"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => setEditingCategory(null)}
                className="p-1.5 rounded-full text-muted-foreground hover:bg-muted transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <span className="font-medium">{category.name}</span>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {category.icon || "smartphone"}
              </span>
            </>
          )}
        </div>
        
        {!isEditing && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => handleStartMove(category)}
              className="p-1.5 rounded-full text-blue-500 hover:bg-blue-500/10 transition-all"
              title="Sposta"
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleStartEdit(category)}
              className="p-1.5 rounded-full text-primary hover:bg-primary/10 transition-all"
              title="Modifica"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDeleteCategory(category)}
              className="p-1.5 rounded-full text-destructive hover:bg-destructive/10 transition-all"
              title="Elimina"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={onClose} shouldScaleBackground={false}>
        <DrawerContent className="h-[85vh] bg-background border-t border-border">
          <DrawerHeader className="relative border-b border-border">
            <button
              onClick={onClose}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <DrawerTitle className="text-xl font-bold text-center">
              Categorie Prodotti
            </DrawerTitle>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Add New Category */}
            <div className="space-y-3 p-4 bg-muted/30 rounded-2xl">
              <h3 className="text-sm font-medium text-foreground">
                Nuova Categoria Prodotti
              </h3>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Nome categoria"
              />
              <Select value={newCategoryIcon} onValueChange={setNewCategoryIcon}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Seleziona icona" />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border">
                  {AVAILABLE_ICONS.map(icon => (
                    <SelectItem key={icon.value} value={icon.value}>
                      {icon.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleAddCategory}
                disabled={isSaving}
                className="w-full gap-2"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Aggiungi Categoria
              </Button>
            </div>

            {/* Categories List */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-foreground px-1">
                Categorie esistenti
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : categories.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed rounded-xl">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nessuna categoria creata</p>
                </div>
              ) : (
                <AnimatePresence>
                  {categories.map((category, index) => renderCategoryItem(category, index))}
                </AnimatePresence>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare "{deleteCategory?.name}". 
              I prodotti associati non saranno più categorizzati.
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Position Dialog */}
      <AlertDialog open={!!movingCategory} onOpenChange={() => setMovingCategory(null)}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Sposta "{movingCategory?.name}"</AlertDialogTitle>
            <AlertDialogDescription>
              Seleziona la nuova posizione per questa categoria.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select value={selectedPosition} onValueChange={setSelectedPosition}>
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Seleziona posizione" />
              </SelectTrigger>
              <SelectContent className="bg-background border border-border">
                {getPositionOptions().map((option) => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className={option.isCurrent ? "font-bold text-primary" : ""}
                  >
                    {option.label} {option.isCurrent && "(attuale)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmMove}
              disabled={!selectedPosition}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Sposta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProductCategoryManager;
